import { WALLET_TYPES, FinancialInstituitionName } from '~/types'

export type WithdrawWalletIdType =
  | WALLET_TYPES.DIGITAL_GOODS
  | WALLET_TYPES.GENERAL
  | WALLET_TYPES.TEMPORARY_HOLDING_BALANCE
  | undefined

export type BlockchainAddress = {
  address: string
  approved: boolean | null
  blockchain: string
  financialInstitutionId: number
  nickname: string
  remainingOfWithdrawalLimit: number
}

export type Wallet = {
  type: WithdrawWalletIdType
  balance: string
  formattedBalance: string
  label: string
  accountId: number
  accountType: string
  fundPoolId: number
}

export type Account = {
  attributes: {
    wallet_id: number
    limitations: {}
    type: string
  }
}

export type AccountLimits = {
  withdrawDailyRemaining: string
  holdingMax: string
}

export type BankAccount = {
  id: number
  bankName: string
  accountNumber: string
  status: 'unverified' | 'verified' | 'pending' | 'rejected'
}

export type FinancialInstituition = {
  id: number
  name: FinancialInstituitionName
  enabled: boolean
}

export enum WITHDRAW_ACTIONS {
  SET_WITHDRAW_WALLET = 'WITHDRAW_ACTIONS.SET_WITHDRAW_WALLET',
  SET_WITHDRAW_AMOUNT = 'WITHDRAW_ACTIONS.SET_WITHDRAW_AMOUNT',
  SET_WITHDRAW_TO_BANK_ID = 'WITHDRAW_ACTIONS.SET_WITHDRAW_TO_BANK_ID',
  SET_CONFIRM_MODAL_OPEN = 'WITHDRAW_ACTIONS.SET_CONFIRM_MODAL_OPEN',
  SET_WITHDRAW_STEP = 'WITHDRAW_ACTIONS.SET_WITHDRAW_STEP',
  SET_2_FA_MODAL_OPEN = 'WITHDRAW_ACTIONS.SET_2_FA_MODAL_OPEN',
  SET_IS_LOADING = 'WITHDRAW_ACTIONS.SET_IS_LOADING',
  GET_ACCOUNT_LIMITS = 'WITHDRAW_ACTIONS.GET_ACCOUNT_LIMITS',
  GET_USER_DETAILS = 'WITHDRAW_ACTIONS.GET_USER_DETAILS',
  CREATE_WITHDRAWAL_REQUEST = 'WITHDRAW_ACTIONS.CREATE_WITHDRAWAL_REQUEST',
  GET_ID_WITHDRAWAL_LIMITS = 'WITHDRAW_ACTIONS.GET_ID_WITHDRAWAL_LIMITS',
  SET_IS_SELECTED_BLOCKCHAIN = 'WITHDRAW_ACTIONS.SET_IS_SELECTED_BLOCKCHAIN',
  SET_IS_ADDRESS_LOADING = 'WITHDRAW_ACTIONS.SET_IS_ADDRESS_LOADING',
  SET_IS_ERC20_FEATURE_GATE_LOADING = 'WITHDRAW_ACTIONS.SET_IS_ERC20_FEATURE_GATE_LOADING',
  SET_IS_ZRC2_FEATURE_GATE_LOADING = 'WITHDRAW_ACTIONS.SET_IS_ZRC2_FEATURE_GATE_LOADING',
  SET_BLOCKCHAIN_ADDRESSES = 'WITHDRAW_ACTIONS.SET_BLOCKCHAIN_ADDRESSES',
  SET_IS_BLOCKCHAIN_WITHDRAW_ENABLED = 'WITHDRAW_ACTIONS.SET_IS_BLOCKCHAIN_WITHDRAW_ENABLED',
  SET_ACCOUNTS = 'WITHDRAW_ACTIONS.SET_ACCOUNTS',
  SET_IS_ERC20_ENABLED = 'WITHDRAW_ACTIONS.SET_IS_ERC20_ENABLED',
  SET_IS_ZRC2_ENABLED = 'WITHDRAW_ACTIONS.SET_IS_ZRC2_ENABLED',
  SET_IS_ZRC2_NOTICE_BANNER_VISIBLE = 'WITHDRAW_ACTIONS.SET_IS_ZRC2_NOTICE_BANNER_VISIBLE',
  SET_IS_STABLECOIN_FEE_LOADING = 'WITHDRAW_ACTIONS.SET_IS_STABLECOIN_FEE_LOADING',
  SET_STABLECOIN_FEE = 'WITHDRAW_ACTIONS.SET_STABLECOIN_FEE',
  SET_IS_ERC20_WITHDRAWAL_FEE_CHARGING = 'WITHDRAW_ACTIONS.SET_IS_ERC20_WITHDRAWAL_FEE_CHARGING',
  SET_IS_ZRC2_WITHDRAWAL_FEE_CHARGING = 'WITHDRAW_ACTIONS.SET_IS_ZRC2_WITHDRAWAL_FEE_CHARGING',
}

export enum WITHDRAW_STEPS {
  DETAILS = 0,
  COMPLETE = 1,
}

type SetWithdrawWallet = {
  type: typeof WITHDRAW_ACTIONS.SET_WITHDRAW_WALLET
  walletId: WithdrawWalletIdType
  accountId: string
  walletName: string | number | undefined
}

type SetWithdrawAmount = {
  type: typeof WITHDRAW_ACTIONS.SET_WITHDRAW_AMOUNT
  amount: string
  withdrawAmountError: string
}

type SetWithdrawToBankId = {
  type: typeof WITHDRAW_ACTIONS.SET_WITHDRAW_TO_BANK_ID
  bankId: number | undefined
}

type SetConfirmModalOpen = {
  type: typeof WITHDRAW_ACTIONS.SET_CONFIRM_MODAL_OPEN
  open: boolean
}

type SetWithdrawStep = {
  type: typeof WITHDRAW_ACTIONS.SET_WITHDRAW_STEP
  step: WITHDRAW_STEPS
}

type Set2FaModalOpen = {
  type: typeof WITHDRAW_ACTIONS.SET_2_FA_MODAL_OPEN
  open: boolean
}

type SetIsLoading = {
  type: typeof WITHDRAW_ACTIONS.SET_IS_LOADING
  isLoading: boolean
}

type GetAccountLimits = {
  type: typeof WITHDRAW_ACTIONS.GET_ACCOUNT_LIMITS
  accountLimits: AccountLimits
}

type GetUserDetails = {
  type: typeof WITHDRAW_ACTIONS.GET_USER_DETAILS
  bankAccounts: BankAccount[]
  has2FaEnabled: boolean
  fullyVerified: boolean
}

type CreateWithdrawalRequest = {
  type: typeof WITHDRAW_ACTIONS.CREATE_WITHDRAWAL_REQUEST
  error: string
  withdrawBankAccount: string
}

type GetIdWithdrawalLimits = {
  type: typeof WITHDRAW_ACTIONS.GET_ID_WITHDRAWAL_LIMITS
  idMinWithdrawal: string
  idMaxWithdrawal: string
}

type SetIsSelectedBlockchain = {
  type: typeof WITHDRAW_ACTIONS.SET_IS_SELECTED_BLOCKCHAIN
  isSelectedBlockchain: boolean
}

type SetBlockchainAddresses = {
  type: typeof WITHDRAW_ACTIONS.SET_BLOCKCHAIN_ADDRESSES
  blockchainAddresses: BlockchainAddress[]
  fiList: Array<FinancialInstituition>
  addressQuota: number
}

type SetIsAddressLoading = {
  type: typeof WITHDRAW_ACTIONS.SET_IS_ADDRESS_LOADING
  isAddressLoading: boolean
}

type SetIsErc20FeatureGateLoading = {
  type: typeof WITHDRAW_ACTIONS.SET_IS_ERC20_FEATURE_GATE_LOADING
  isErc20FeatureGateLoading: boolean
}

type SetIsZrc2FeatureGateLoading = {
  type: typeof WITHDRAW_ACTIONS.SET_IS_ZRC2_FEATURE_GATE_LOADING
  isZrc2FeatureGateLoading: boolean
}

type SetIsBlockchainWithdrawEnabled = {
  type: typeof WITHDRAW_ACTIONS.SET_IS_BLOCKCHAIN_WITHDRAW_ENABLED
  isBlockchainWithdrawEnabled: boolean
}

type SetAccounts = {
  type: typeof WITHDRAW_ACTIONS.SET_ACCOUNTS
  accounts: { [key: string]: Account }
}

type SetIsErc20Enabled = {
  type: typeof WITHDRAW_ACTIONS.SET_IS_ERC20_ENABLED
  isErc20Enabled: boolean
}

type SetIsZrc2Enabled = {
  type: typeof WITHDRAW_ACTIONS.SET_IS_ZRC2_ENABLED
  isZrc2Enabled: boolean
}

type SetIsZrc2NoticeBannerEnabled = {
  type: typeof WITHDRAW_ACTIONS.SET_IS_ZRC2_NOTICE_BANNER_VISIBLE
  isZrc2NoticeBannerVisible: boolean
}

type SetIsStablecoinFeeLoading = {
  type: typeof WITHDRAW_ACTIONS.SET_IS_STABLECOIN_FEE_LOADING
  isStablecoinFeeLoading: boolean
}

type SetStablecoinFee = {
  type: typeof WITHDRAW_ACTIONS.SET_STABLECOIN_FEE
  amount: number
  identifier: string
  timestamp: string
}

type SetIsErc20WithdrawalFeeCharging = {
  type: typeof WITHDRAW_ACTIONS.SET_IS_ERC20_WITHDRAWAL_FEE_CHARGING
  isErc20WithdrawalFeeCharging: boolean
}

type SetIsZrc2WithdrawalFeeCharging = {
  type: typeof WITHDRAW_ACTIONS.SET_IS_ZRC2_WITHDRAWAL_FEE_CHARGING
  isZrc2WithdrawalFeeCharging: boolean
}

export type ActionTypes =
  | SetWithdrawWallet
  | SetWithdrawAmount
  | SetWithdrawToBankId
  | SetConfirmModalOpen
  | SetWithdrawStep
  | Set2FaModalOpen
  | SetIsLoading
  | GetAccountLimits
  | GetUserDetails
  | CreateWithdrawalRequest
  | GetIdWithdrawalLimits
  | SetIsSelectedBlockchain
  | SetBlockchainAddresses
  | SetIsAddressLoading
  | SetIsErc20FeatureGateLoading
  | SetIsZrc2FeatureGateLoading
  | SetIsBlockchainWithdrawEnabled
  | SetAccounts
  | SetIsErc20Enabled
  | SetIsZrc2Enabled
  | SetIsZrc2NoticeBannerEnabled
  | SetIsStablecoinFeeLoading
  | SetStablecoinFee
  | SetIsErc20WithdrawalFeeCharging
  | SetIsZrc2WithdrawalFeeCharging
